import { buildDirectReportsMap, OrgChartRow } from './orgScope';

export type TargetType = 'user_group' | 'azure_ad_group';

/** Every user below `rootUserId` in the management chain, at any depth. The root is excluded. */
export function subtreeUserRows(rows: OrgChartRow[], rootUserId: number | null | undefined): OrgChartRow[] {
  if (rootUserId == null) return [];

  const { byManager } = buildDirectReportsMap(rows);
  const out: OrgChartRow[] = [];
  const visited = new Set<number>([rootUserId]);
  const queue: number[] = [rootUserId];

  while (queue.length) {
    const current = queue.shift() as number;
    for (const child of byManager.get(current) ?? []) {
      if (child?.user_id == null || visited.has(child.user_id)) continue;
      visited.add(child.user_id);
      out.push(child);
      queue.push(child.user_id);
    }
  }

  return out;
}

/**
 * Whether a manager has to treat an existing assignment as read-only. Admins are never locked.
 * A manager owns what they or anyone beneath them created; anything else (an admin or their own
 * manager) is locked. A row from before assigned_by was recorded stays editable when it was a
 * manager-sourced assignment, and is locked otherwise. The server enforces the same rule.
 */
export function isAssignmentLocked(args: {
  isAdmin: boolean;
  actorId: number | string | null | undefined;
  assignedBy: number | string | null | undefined;
  sourceType?: string | null;
  subtreeIds: Set<number>;
}): boolean {
  if (args.isAdmin) return false;
  if (args.assignedBy == null || args.assignedBy === '') return args.sourceType !== 'manager';

  const assignedBy = Number(args.assignedBy);
  if (args.actorId != null && assignedBy === Number(args.actorId)) return false;

  return !args.subtreeIds.has(assignedBy);
}

/** Required assignments notify by default; optional ones stay quiet unless the assigner opts in. */
export function effectiveSendNotification(isRequired: boolean, override: boolean | null): boolean {
  return override ?? isRequired;
}

export const LOCKED_TOOLTIP = 'Assigned by an admin or your manager. You cannot change it.';

export const requiredLabel = (isRequired?: boolean | null) => (isRequired ? 'Required' : 'Optional');
